import React from 'react';
import { IEditableColumn } from '../helpers/interfaces/payments-reports';

interface EditableTableProps {
    columns: IEditableColumn[];
    rows: any[];
    onRowsChange: (rows: any[]) => void;
    readOnly: boolean;
    disabled?: boolean;
    tabIndex: number;
    adminColumns?: string[];
    showRowActions?: boolean;
    showAddButton?: boolean;
    addButtonLabel?: string;
    footerContent?: React.ReactNode;
    rowIdPrefix?: string;
    rowNumberOffset?: number;
    maxHeight?: string;
    extraRowAction?: {
        title: string;
        iconClassName: string;
        onClick: (rowIndex: number) => void;
    };
}

interface EditableTableState {
    editingCell: { row: number; col: string } | null;
    editValue: string;
}

export default class EditableTable extends React.Component<EditableTableProps, EditableTableState> {
    private tableRef: React.RefObject<HTMLTableElement>;

    constructor(props: EditableTableProps) {
        super(props);
        this.tableRef = React.createRef();
        this.state = {
            editingCell: null,
            editValue: '',
        };
    }

    isCellReadOnly = (colKey: string): boolean => {
        if (this.props.readOnly || this.props.disabled) return true;
        const { adminColumns } = this.props;
        if (adminColumns && adminColumns.includes(colKey)) return true;
        return false;
    };

    handleCellClick = (rowIndex: number, colKey: string, currentValue: any) => {
        if (this.isCellReadOnly(colKey)) return;
        this.setState({
            editingCell: { row: rowIndex, col: colKey },
            editValue: currentValue !== null && currentValue !== undefined ? String(currentValue) : '',
        });
    };

    handleEditChange = (value: string) => {
        this.setState({ editValue: value });
    };

    commitEdit = () => {
        const { editingCell, editValue } = this.state;
        const { rows, onRowsChange } = this.props;
        if (!editingCell) return;

        const newRows = [...rows];
        const colDef = this.props.columns.find(c => c.key === editingCell.col);
        let parsedValue: any = editValue;

        if (colDef) {
            if (colDef.type === 'number') {
                parsedValue = editValue === '' ? null : parseFloat(editValue.replace(',', '.'));
                if (isNaN(parsedValue)) parsedValue = null;
            } else if (colDef.type === 'integer') {
                const numberValue = editValue === '' ? null : Number(editValue);
                parsedValue = numberValue !== null && Number.isInteger(numberValue) ? numberValue : null;
            } else if (colDef.type === 'date') {
                parsedValue = editValue === '' ? null : editValue;
            }
        }

        newRows[editingCell.row] = {
            ...newRows[editingCell.row],
            [editingCell.col]: parsedValue,
        };

        onRowsChange(newRows);
        this.setState({ editingCell: null, editValue: '' });
    };

    cancelEdit = () => {
        this.setState({ editingCell: null, editValue: '' });
    };

    handleKeyDown = (e: React.KeyboardEvent, rowIndex: number, colKey: string) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            this.commitEdit();
            const nextRow = rowIndex + 1;
            if (nextRow < this.props.rows.length) {
                setTimeout(() => this.handleCellClick(nextRow, colKey, this.props.rows[nextRow][colKey]), 0);
            }
        } else if (e.key === 'Tab') {
            e.preventDefault();
            this.commitEdit();
            const cols = this.props.columns.map(c => c.key);
            const currentIndex = cols.indexOf(colKey);
            const nextIndex = e.shiftKey ? currentIndex - 1 : currentIndex + 1;
            if (nextIndex >= 0 && nextIndex < cols.length) {
                const nextCol = cols[nextIndex];
                setTimeout(() => this.handleCellClick(rowIndex, nextCol, this.props.rows[rowIndex][nextCol]), 0);
            } else if (nextIndex >= cols.length && rowIndex + 1 < this.props.rows.length) {
                setTimeout(() => this.handleCellClick(rowIndex + 1, cols[0], this.props.rows[rowIndex + 1][cols[0]]), 0);
            }
        } else if (e.key === 'Escape') {
            this.cancelEdit();
        }
    };

    addRow = () => {
        if (this.props.disabled) return;
        const { rows, onRowsChange } = this.props;
        const newRow: any = { sortOrder: rows.length };
        this.props.columns.forEach(col => {
            if (col.type === 'number' || col.type === 'integer') newRow[col.key] = null;
            else newRow[col.key] = '';
        });
        onRowsChange([...rows, newRow]);
    };

    deleteRow = (rowIndex: number) => {
        if (this.props.disabled) return;
        const { rows, onRowsChange } = this.props;
        const newRows = rows.filter((_, i) => i !== rowIndex);
        onRowsChange(newRows);
    };

    formatValue = (value: any, type: string): string => {
        if (value === null || value === undefined) return '';
        if (type === 'number' && typeof value === 'number') {
            return Number(value).toFixed(2);
        }
        if (type === 'integer' && value !== '') {
            return String(Number(value));
        }
        return String(value);
    };

    handlePaste = (e: React.ClipboardEvent) => {
        if (this.props.readOnly || this.props.disabled) return;
        const clipboardData = e.clipboardData.getData('text');
        if (!clipboardData) return;

        const lines = clipboardData.trim().split('\n');
        if (lines.length < 2) return;

        e.preventDefault();

        const { rows, onRowsChange } = this.props;
        const newRows = [...rows];
        const cols = this.props.columns.map(c => c.key);

        const startRow = this.state.editingCell ? this.state.editingCell.row : rows.length;
        const startCol = this.state.editingCell ? cols.indexOf(this.state.editingCell.col) : 0;

        lines.forEach((line, lineIndex) => {
            const values = line.split('\t');
            const targetRow = startRow + lineIndex;

            if (targetRow >= newRows.length) {
                const newRow: any = { sortOrder: targetRow };
                cols.forEach(col => { newRow[col] = ''; });
                newRows.push(newRow);
            }

            values.forEach((value, colIndex) => {
                const colKey = cols[startCol + colIndex];
                if (colKey === undefined || colKey === null) return;
                const colDef = this.props.columns.find(c => c.key === colKey);
                if (!colDef) return;

                let parsedValue: any = value.trim();
                if (colDef.type === 'number') {
                    parsedValue = parsedValue === '' ? null : parseFloat(parsedValue.replace(',', '.'));
                    if (isNaN(parsedValue)) parsedValue = parsedValue === null ? null : value.trim();
                } else if (colDef.type === 'integer') {
                    const numberValue = parsedValue === '' ? null : Number(parsedValue);
                    parsedValue = numberValue !== null && Number.isInteger(numberValue) ? numberValue : null;
                }
                newRows[targetRow][colKey] = parsedValue;
            });
        });

        onRowsChange(newRows);
        this.setState({ editingCell: null, editValue: '' });
    };

    renderCell = (row: any, rowIndex: number, col: IEditableColumn) => {
        const { editingCell, editValue } = this.state;
        const isEditing = editingCell !== null && editingCell.row === rowIndex && editingCell.col === col.key;

        if (isEditing) {
            if (col.type === 'select' && col.values) {
                return (
                    <select
                        className="form-control form-control-sm"
                        value={editValue}
                        onChange={(e) => this.handleEditChange(e.target.value)}
                        onBlur={this.commitEdit}
                        onKeyDown={(e) => this.handleKeyDown(e, rowIndex, col.key)}
                        autoFocus
                        style={{ width: '100%', minWidth: col.width || '80px' }}
                    >
                        <option value="">--</option>
                        {col.values.map((v: { key: string; value: string }) => (
                            <option key={v.key} value={v.key}>{v.value}</option>
                        ))}
                    </select>
                );
            }

            if (col.type === 'date') {
                return (
                    <input
                        type="date"
                        className="form-control form-control-sm"
                        value={editValue}
                        onChange={(e) => this.handleEditChange(e.target.value)}
                        onBlur={this.commitEdit}
                        onKeyDown={(e) => this.handleKeyDown(e, rowIndex, col.key)}
                        autoFocus
                        style={{ width: '100%', minWidth: col.width || '120px' }}
                    />
                );
            }

            return (
                <input
                    type={col.type === 'number' || col.type === 'integer' ? 'number' : col.type === 'email' ? 'email' : 'text'}
                    className="form-control form-control-sm"
                    value={editValue}
                    onChange={(e) => this.handleEditChange(e.target.value)}
                    onBlur={this.commitEdit}
                    onKeyDown={(e) => this.handleKeyDown(e, rowIndex, col.key)}
                    autoFocus
                    step={col.type === 'number' ? '0.01' : col.type === 'integer' ? '1' : undefined}
                    style={{ width: '100%', minWidth: col.width || '80px' }}
                />
            );
        }

        const value = row[col.key];
        const displayValue = this.formatValue(value, col.type);
        const cellReadOnly = this.props.readOnly
            || Boolean(this.props.adminColumns && this.props.adminColumns.includes(col.key));
        const cellDisabled = cellReadOnly || Boolean(this.props.disabled);
        const cellClass = cellReadOnly ? 'editable-cell readonly' : 'editable-cell';

        return (
            <div
                className={cellClass}
                onClick={() => this.handleCellClick(rowIndex, col.key, value)}
                style={{ minHeight: '24px', cursor: cellDisabled ? 'default' : 'cell', padding: '2px 4px' }}
            >
                {displayValue || '\u00A0'}
            </div>
        );
    };

    render() {
        const { columns, rows, readOnly, disabled, extraRowAction, footerContent, rowIdPrefix, rowNumberOffset = 0, maxHeight } = this.props;
        const showRowActions = this.props.showRowActions !== undefined ? this.props.showRowActions : !readOnly;
        const showAddButton = this.props.showAddButton !== undefined ? this.props.showAddButton : !readOnly;
        const stickyHeaderStyle = maxHeight
            ? { position: 'sticky' as const, top: 0, zIndex: 1, background: '#f4f6f8' }
            : undefined;

        return (
            <div
                className="table-responsive"
                onPaste={this.handlePaste}
                style={maxHeight ? { maxHeight, overflow: 'auto' } : undefined}
            >
                <table className="table table-bordered table-sm table-hover" ref={this.tableRef}
                    style={{ fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                    <thead className="thead-light">
                        <tr>
                            {showRowActions && (
                                <th style={{ width: extraRowAction ? '72px' : '40px', ...stickyHeaderStyle }}></th>
                            )}
                            <th style={{ width: '40px', ...stickyHeaderStyle }}>#</th>
                            {columns.map(col => (
                                <th key={col.key} style={{ width: col.width, minWidth: '80px', ...stickyHeaderStyle }}>
                                    {col.title}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, rowIndex) => (
                            <tr key={rowIndex} id={rowIdPrefix ? rowIdPrefix + rowIndex : undefined}>
                                {showRowActions && (
                                    <td className="text-center align-middle" style={{ padding: '3px 4px', whiteSpace: 'nowrap' }}>
                                        <div className="d-inline-flex align-items-center justify-content-center" style={{ gap: '6px' }}>
                                        {extraRowAction && (
                                            <button
                                                type="button"
                                                className="btn btn-sm topic-empty-btn-color d-inline-flex align-items-center justify-content-center"
                                                disabled={disabled}
                                                onClick={() => extraRowAction.onClick(rowIndex)}
                                                title={extraRowAction.title}
                                                aria-label={extraRowAction.title}
                                                style={{ width: '24px', height: '24px', padding: 0, fontSize: '0.78rem' }}
                                            >
                                                <i className={extraRowAction.iconClassName} />
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-danger d-inline-flex align-items-center justify-content-center"
                                            disabled={disabled}
                                            onClick={() => this.deleteRow(rowIndex)}
                                            title="Elimina riga"
                                            aria-label="Elimina riga"
                                            style={{ width: '24px', height: '24px', padding: 0, fontSize: '0.78rem' }}
                                        >
                                            <i className="fa fa-times" />
                                        </button>
                                        </div>
                                    </td>
                                )}
                                <td className="text-center text-muted" style={{ padding: '2px 4px', fontSize: '0.8rem' }}>
                                    {rowNumberOffset + rowIndex + 1}
                                </td>
                                {columns.map(col => (
                                    <td key={col.key} style={{ padding: '1px' }}>
                                        {this.renderCell(row, rowIndex, col)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                        {rows.length === 0 && (
                            <tr>
                                <td colSpan={columns.length + (showRowActions ? 2 : 1)} className="text-center text-muted py-4">
                                    Nessuna riga.{' '}
                                    {!readOnly && showAddButton && (
                                        <span>
                                            Clicca "Aggiungi riga" o incolla dati da Excel (Ctrl+V).
                                        </span>
                                    )}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
                {(showAddButton || footerContent) && (
                    <div className="d-flex align-items-center flex-wrap mt-2" style={{ gap: '8px' }}>
                        {showAddButton && (
                            <button type="button" className="btn topic-empty-btn-color" onClick={this.addRow} disabled={disabled}>
                                <i className="fa fa-plus mr-1" /> {this.props.addButtonLabel || 'Aggiungi riga'}
                            </button>
                        )}
                        {footerContent}
                    </div>
                )}
            </div>
        );
    }
}
